import { Pipe, PipeTransform } from '@angular/core';
import { marked, Tokens } from 'marked';
@Pipe({
  name: 'markdown',
  standalone: true,
})
export class MarkdownPipe implements PipeTransform {
  private latexPlaceholders: Map<string, string> = new Map();
  private placeholderCounter = 0;

  constructor() {
    // Inline math: \( ... \)
    const inlineMath = {
      name: 'inline-math',
      level: 'inline' as const,
      start(src: string) {
        const m = src.match(/\\\(/);
        return m ? m.index! : -1;
      },
      // Teaches the markdown parser to recognize a new type of content that isn't standard markdown.
      // In this case, it teaches marked to recognize inline LaTeX math delimited by \( and \)
      /**
       * Example: Full markdown: "The equation \\(x^2 + 1\\) is simple."
                                               ↑
                                            start() found \( here
       * Tokenizer receives: "\\(x^2 + 1\\) is simple."
       * Starting from the beginning, match \(, then capture everything until \)     
       * The tokenizer teaches marked.js to recognize \(LaTeX\) as a special inline token
       * It uses regex to match from the current position, captures the LaTeX content, and returns a token object.      
       * Keeps math expressions intact for MathJax to render later.
       * The ^ anchor means it only matches if \( is right at the current parsing position, and raw tells marked exactly how many characters to consume and move past.                             
       */
      tokenizer(src: string) {
        const m = src.match(/^\\\(([\s\S]+?)\\\)/);
        if (!m) return;
        return { type: 'inline-math', raw: m[0], text: m[0] };
      },
      renderer(token: any) { return token.text; },
    };

    // Block math: \[ ... \]
    const blockMath = {
      name: 'block-math',
      level: 'block' as const,
      start(src: string) {
        const m = src.match(/\\\[/);
        return m ? m.index! : -1;
      },
      // In this case, it teaches marked to recognize inline LaTeX math delimited by \[ and \]
      /**
       * Example: Full markdown: "The equation \\[x^2 + 1\\] is simple."
                                               ↑
                                            start() found \[ here
       * Tokenizer receives: "\\[x^2 + 1\\] is simple."
       * Starting from the beginning, match \[, then capture everything until \]     
       * The tokenizer teaches marked.js to recognize \[LaTeX\] as a special inline token
       * It uses regex to match from the current position, captures the LaTeX content, and returns a token object.      
       * Keeps math expressions intact for MathJax to render later.
       * The ^ anchor means it only matches if \[ is right at the current parsing position, and raw tells marked exactly how many characters to consume and move past.                             
       */
      tokenizer(src: string) {
        const m = src.match(/^\\\[((?:.|\n)*?)\\\]\s*/);
        if (!m) return;
        return { type: 'block-math', raw: m[0], text: m[0] };
      },
      renderer(token: any) { return token.text; },
    };

    marked.use({ extensions: [inlineMath, blockMath] });
    marked.use({
      gfm: true,
      breaks: true,
      mangle: false,
      headerIds: false,
      smartLists: true,
      smartypants: false, // Keeps ALL punctuation as-is for math since math uses dashes as Negative numbers: -5, Subtraction: x - y, Ranges: x ∈ [0--10] or Operators in formulas
    } as any);
  }

  transform(value: string | null): string {
    if (!value) return '';

    // Reset placeholders for each transform
    this.latexPlaceholders.clear();
    this.placeholderCounter = 0;

    // Step 0: this is only for MathJax in DisplayMath ex: \[ \] we are replacing \align with \aligned
    // Note: $ and $$ are not used as math delimiters
    value = this.replaceAlignWithAlignedInDisplayMath(value);

    // Step 1: Protect LaTeX from markdown parser
    let protectedText = this.protectLatex(value);

    // Step 2: Parse markdown
    const html = marked.parse(protectedText).toString();

    // Step 3: Restore LaTeX placeholders
    let restored = this.restoreLatex(html);

    // Step 4: Wrap tables via DOM
    // This is specifically for mobile since tables can be wider than the screen on mobile devices 
    // So without the wrapper tables may overflow the screen or content gets cut off
    /**
     * Create a Temporary Container
     * Find All Tables
     * Create a Wrapper Div
     * Insert Wrapper Before Table
     * Move Table Inside Wrapper
     */
    const container = document.createElement('div');
    container.innerHTML = restored;
    container.querySelectorAll('table').forEach((tbl) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'table-wrapper';
      tbl.parentNode?.insertBefore(wrapper, tbl);
      wrapper.appendChild(tbl);
    });

    return container.innerHTML;
  }

  /**
   * Protects LaTeX expressions from markdown parsing by replacing them with placeholders
   * Example Input: "Here's a table:\n| x | y |\n|---|---|\n| $2x$ | \\hline |\n\nAnd inline: \\(a^2\\)"
   * Problem: The markdown parser will:
      - Escape backslashes: \\ → \\\\
      - HTML-encode characters inside table cells
      - Treat LaTeX commands as plain text
   * After protection: "Here's a table:\n| x | y |\n|---|---|\n| LATEX_PLACEHOLDER_0_ENDLATEX | LATEX_PLACEHOLDER_1_ENDLATEX |\n\nAnd inline: LATEX_PLACEHOLDER_2_ENDLATEX"
   * What it does:
      1. Finds display math \[ ... \] → LATEX_PLACEHOLDER_0_ENDLATEX
      2. Finds inline math \( ... \) → LATEX_PLACEHOLDER_1_ENDLATEX
      5. Finds LaTeX commands \hline, \cline{1-2} → LATEX_PLACEHOLDER_4_ENDLATEX
      
      These placeholders are just plain text that markdown won't ruin.
   */
  private protectLatex(text: string): string {
    // Protect display math: \[ ... \]
    text = text.replace(/\\\[([\s\S]*?)\\\]/g, (match) => {
      return this.createPlaceholder(match);
    });

    // Protect inline math: \( ... \)
    text = text.replace(/\\\(([\s\S]*?)\\\)/g, (match) => {
      return this.createPlaceholder(match);
    });

    // Protect standalone LaTeX commands that might appear in tables
    // This catches things like \hline, \cline, etc.
    /**
     * Finds and protects standalone LaTeX commands that might get mangled by the markdown parser.
    \\ - Finds a backslash
    [a-zA-Z]+ - Followed by letters (the command name like "hline", "cline")
    (?:\{[^}]*\}|\[[^\]]*\])* - Optionally followed by curly braces {...} or square brackets [...]
    Examples it catches:
      \hline → Simple command
      \cline{1-2} → Command with argument
      \textbf{bold} → Formatting command
      \color{red} → Color command
      \item[label] → Command with optional argument
     */
    text = text.replace(/\\[a-zA-Z]+(?:\{[^}]*\}|\[[^\]]*\])*/g, (match) => {
      // Don't protect markdown-safe commands
      if (match.match(/^\\[(\[]$/)) return match;
      return this.createPlaceholder(match);
    });

    return text;
  }

  /**
   * Creates a unique placeholder for LaTeX content
   */
  private createPlaceholder(latex: string): string {
    const id = `LATEX_PLACEHOLDER_${this.placeholderCounter++}_ENDLATEX`;
    this.latexPlaceholders.set(id, latex);
    return id;
  }

  /**
   * Restores LaTeX placeholders back to their original Latex
   */
  private restoreLatex(html: string): string {
    this.latexPlaceholders.forEach((latex, placeholder) => {
      const normalizedLatex = this.stripLeadingBlockquoteMarkers(latex);
      html = html.replace(new RegExp(placeholder, 'g'), normalizedLatex);
    });
    return html;
  }

  private stripLeadingBlockquoteMarkers(latex: string): string {
    if (!latex.startsWith('\\[')) {
      return latex;
    }

    return latex
      .split('\n')
      .map((line) => {
        line = line.trimStart();
        if (!line.startsWith('>')) {
          return line;
        }

        line = line.replace('>','').trimStart();

        return line;
      })
      .join('\n');
  }

  /**
   * Replace \begin{align[*]}...\end{align[*]} with \begin{aligned}...\end{aligned}
   * ONLY inside display math blocks (\[...\]).
   *
   * - Leaves inline math unchanged.
   * - Handles multiple blocks in the same text.
   * - Does NOT process $$ delimiters ($ and $$ should not be used as math delimiters).
   *
   * Caveats:
   * - Removes numbering (aligned is non-numbered).
   * - Does not attempt to balance nested math; assumes well-formed blocks.
   */
  private replaceAlignWithAlignedInDisplayMath(input: string): string {
    if (!input) return input;

    // Helper to rewrite align environments inside a captured math block
    const rewriteAlignEnv = (mathBlock: string): string => {
      return mathBlock
        // \begin{align} or \begin{align*} -> \begin{aligned}
        .replace(/\\begin\{align\*?\}/g, '\\begin{aligned}')
        // \ end{align} or \end{align*} -> \end{aligned}
        .replace(/\\end\{align\*?\}/g, '\\end{aligned}');
    };

    // Replace inside \[...\] (display math)
    // Note: $ and $$ are not used as math delimiters
    const replacedDisplayBracket = input.replace(
      /\\\[[\s\S]*?\\\]/g,
      (block) => rewriteAlignEnv(block)
    );

    return replacedDisplayBracket;
  }

}
